"use client";

import React from "react";
import { Mail, MapPin, Building2 } from "lucide-react";
import { Navbar } from "@/components/ui/navbar";
import { Footer } from "@/components/ui/footer";
import {
	ThemeBackground,
	ThemeCard,
	ThemeText,
	AuroraBlobs,
} from "@/components/ui/theme-utils";
import { PrimaryButton } from "@/components/ui/buttons";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

export default function ContactPage() {
	const { isDark } = useTheme();

	return (
		<ThemeBackground className="min-h-screen flex flex-col">
			<Navbar />

			<main className="flex-grow">
				<section className="relative overflow-hidden pt-40 sm:pt-48 pb-12 sm:pb-16">
					<div
						className={cn(
							"absolute inset-0 -z-10",
							isDark ? "bg-mesh-dark" : "bg-mesh-light"
						)}
					/>
					<div
						className={cn(
							"absolute inset-0 -z-10 opacity-40",
							"bg-grid"
						)}
					/>
					<AuroraBlobs variant="mixed" />

					<div className="container relative z-10 mx-auto px-5 sm:px-6">
						<div className="mx-auto max-w-4xl">
							<div className="text-center mb-10">
								<h1 className="responsive-heading-2">
									Contact <span className="text-gradient">Us</span>
								</h1>
								<ThemeText variant="subtle" className="mt-6 text-lg sm:text-xl leading-relaxed">
									We'd love to hear from you. Get in touch with us using the details below.
								</ThemeText>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
								{/* Left Side: Context / Message */}
								<div className="flex flex-col justify-center">
									<h2 className="responsive-heading-3 mb-4">Get in Touch</h2>
									<ThemeText variant="subtle" className="mb-8 text-lg">
										Whether you have a question about features, pricing, or anything else, our team is ready to answer all your questions.
									</ThemeText>

									<div className="space-y-6">
										<div className="flex items-start space-x-4">
											<div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 shrink-0">
												<Building2 className="h-6 w-6" />
											</div>
											<div>
												<h3 className="font-semibold text-lg">Business Name</h3>
												<ThemeText variant="muted">Novarise</ThemeText>
											</div>
										</div>

										<div className="flex items-start space-x-4">
											<div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 shrink-0">
												<Mail className="h-6 w-6" />
											</div>
											<div>
												<h3 className="font-semibold text-lg">Email / Contact</h3>
												<a
													href="mailto:info@zset.in"
													className="hover:text-brand-500 transition-colors"
												>
													<ThemeText variant="muted">info@zset.in</ThemeText>
												</a>
											</div>
										</div>

										<div className="flex items-start space-x-4">
											<div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-glow-sm ring-1 ring-inset ring-white/10 shrink-0">
												<MapPin className="h-6 w-6" />
											</div>
											<div>
												<h3 className="font-semibold text-lg">Operating Address</h3>
												<ThemeText variant="muted">
													Near BLM Enter college,<br />
													Kanpur 208007
												</ThemeText>
											</div>
										</div>
									</div>
								</div>

								{/* Right Side: Card Display */}
								<ThemeCard className="h-full flex items-center justify-center p-10 sm:p-12 relative overflow-hidden">
									<div className="absolute top-0 right-0 w-64 h-64 bg-brand-700/10 rounded-full blur-3xl -mr-32 -mt-32" />
									<div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-500/10 rounded-full blur-3xl -ml-32 -mb-32" />

									<div className="relative z-10 text-center">
										<div className="mb-6 mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center shadow-glow-sm ring-1 ring-inset ring-white/10">
											<Mail className="h-10 w-10 text-white" />
										</div>
										<h3 className="responsive-heading-3 mb-3">Message us directly</h3>
										<ThemeText variant="subtle" className="mb-6">
											Prefer email? Drop us a line at
										</ThemeText>
										<PrimaryButton
											href="mailto:info@zset.in"
											icon={<Mail className="h-4 w-4" />}
										>
											info@zset.in
										</PrimaryButton>
									</div>
								</ThemeCard>
							</div>
						</div>
					</div>
				</section>
			</main>

			<Footer isHomePage={false} />
		</ThemeBackground>
	);
}
